import { useCallback, useEffect, useState } from 'react'
import { evaluate } from 'mathjs'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generatePemdasEquation } from '~/bootcamp/Activities/PEMDAS/utils/pemdas.utils'

import Equation from '~/bootcamp/Activities/PEMDAS/components/Equation'

import { OperatorSignType } from '~/bootcamp/Activities/PEMDAS/types'

import { solveTerm, checkPossibility } from './helper'

function OrderPicker({
  difficultyLevel,
  actionButtonsFC: AnswerButton,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const [equation, setEquation] = useState<string>('')
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [pattern, setPattern] = useState<string>('')
  const [flag, setFlag] = useState<boolean>(true)

  // The equation as generated: `equation` itself mutates as the student solves term by term.
  const [generatedEquation, setGeneratedEquation] = useState<string>('')

  const generateEquation = useCallback(() => {
    const { equation: newEquation, pattern: newPattern } = generatePemdasEquation(difficultyLevel, taskHistory)

    setEquation(newEquation)
    setPattern(newPattern)
    setGeneratedEquation(newEquation)
  }, [difficultyLevel, taskHistory])

  useEmitInstance(onInstanceChange, generatedEquation || null)

  useEffect(() => {
    generateEquation()
  }, [generateEquation])

  const clearAnswer = (): void => {
    setFlag(true)
    onAnswerStateChange(null)
  }

  const checkAnswer = (): void => {
    const isCorrect = parseInt(equation, 10) === evaluate(equation)
    onAnswerStateChange(isCorrect)
  }

  const nextQuestionHandler = (): void => {
    clearAnswer()
    setTaskHistory((currHistory) => {
      return [pattern, ...currHistory].slice(0, 3)
    })
    generateEquation()
    onNextQuestionClick()
  }

  const handleOperatorClick = (indexInEquation: number, type: OperatorSignType): void => {
    if (checkPossibility(equation, indexInEquation, type)) {
      setEquation(solveTerm(equation, indexInEquation, type))
      setFlag(true)
      if (answerState === false) onAnswerStateChange(null)
    } else {
      setFlag(false)
    }
  }

  return (
    <ActivityWrapper className="justify-between">
      <span />
      <div className="flex flex-wrap items-center space-x-1 justify-center">
        <Equation isWrong={answerState === false || !flag} onOperatorClick={handleOperatorClick} statement={equation} />
      </div>

      {AnswerButton && (
        <AnswerButton
          clearFormAction={clearAnswer}
          correctAnswer={answerState ?? false}
          formValid
          goToNextQuestionAction={nextQuestionHandler}
          submitAnswerAction={checkAnswer}
        />
      )}
    </ActivityWrapper>
  )
}

export default OrderPicker
